/**
 * Auth endpoints (U6) — the typed client for /api/v1/auth/*.
 *
 * The login flow is a state machine driven by the shared discriminated union:
 * `ok` | `totp_required` | `totp_setup_required` (TOTP mandatory for
 * owner/admin — see shared/src/schemas/auth.ts). `completeLogin()` stores the
 * tokens and hydrates identity from /auth/me.
 */
import { z } from 'zod';
import {
  AuthConfigResponseSchema,
  GoogleLoginResponseSchema,
  LoginResponseSchema,
  MeResponseSchema,
  OkResponseSchema,
  RefreshResponseSchema,
  SURFACE_NOT_ALLOWED,
  SessionsListResponseSchema,
  SignupResponseSchema,
  TotpActivateResponseSchema,
  TotpLoginResponseSchema,
  TotpSetupResponseSchema,
  type GoogleSignupRequest,
  type LoginRequest,
  type MeResponse,
  type SignupRequest,
  type TotpActivateRequest,
  type TotpLoginRequest,
  type TotpSetupRequest,
} from '@buildledger/shared';
import { ApiRequestError, request } from './http';
import { useSession } from './session';

type LoginOk = z.infer<typeof TotpLoginResponseSchema>;

export const authApi = {
  signup: (body: SignupRequest) =>
    request({
      method: 'POST',
      path: '/auth/signup',
      body,
      schema: SignupResponseSchema,
      auth: false,
    }),

  /** The panel always signs in as the `panel` client (roles without that surface get 403). */
  login: (body: Omit<LoginRequest, 'client'>) =>
    request({
      method: 'POST',
      path: '/auth/login',
      body: { ...body, client: 'panel' } satisfies LoginRequest,
      schema: LoginResponseSchema,
      auth: false,
    }),

  loginTotp: (body: TotpLoginRequest) =>
    request({
      method: 'POST',
      path: '/auth/login/totp',
      body,
      schema: TotpLoginResponseSchema,
      auth: false,
    }),

  /** Reachable with a bearer session OR a totp_setup_required challenge token. */
  totpSetup: (body: TotpSetupRequest) =>
    request({
      method: 'POST',
      path: '/auth/totp/setup',
      body,
      schema: TotpSetupResponseSchema,
      auth: body.totpToken === undefined,
    }),

  totpActivate: (body: TotpActivateRequest) =>
    request({
      method: 'POST',
      path: '/auth/totp/activate',
      body,
      schema: TotpActivateResponseSchema,
      auth: body.totpToken === undefined,
    }),

  refresh: (refreshToken: string) =>
    request({
      method: 'POST',
      path: '/auth/refresh',
      body: { refreshToken },
      schema: RefreshResponseSchema,
      auth: false,
    }),

  logout: () => request({ method: 'POST', path: '/auth/logout', schema: OkResponseSchema }),

  me: () => request({ path: '/auth/me', schema: MeResponseSchema }),

  sessions: () => request({ path: '/auth/sessions', schema: SessionsListResponseSchema }),

  revokeSession: (id: string) =>
    request({ method: 'DELETE', path: `/auth/sessions/${id}`, schema: OkResponseSchema }),

  /** What the sign-in screens may offer — `google.webClientId` null = no Google button. */
  config: () => request({ path: '/auth/config', schema: AuthConfigResponseSchema, auth: false }),

  /** "Continue with Google" — a Google ID token in, the same login states out, plus `signup_required`. */
  google: (idToken: string) =>
    request({
      method: 'POST',
      path: '/auth/google',
      body: { idToken, client: 'panel' },
      schema: GoogleLoginResponseSchema,
      auth: false,
    }),

  /** Finish a Google sign-up: company details + the signed token from `signup_required`. */
  googleSignup: (body: Omit<GoogleSignupRequest, 'client'>) =>
    request({
      method: 'POST',
      path: '/auth/google/signup',
      body: { ...body, client: 'panel' } satisfies GoogleSignupRequest,
      schema: SignupResponseSchema,
      auth: false,
    }),
};

// -------------------------------------------------------------- flow helpers

/**
 * The server's message when this account may not sign in on the panel
 * (403 SURFACE_NOT_ALLOWED, e.g. a Worker) — null for any other error.
 */
export function surfaceNotAllowedMessage(error: unknown): string | null {
  if (
    error instanceof ApiRequestError &&
    error.statusCode === 403 &&
    error.code === SURFACE_NOT_ALLOWED
  ) {
    return error.messages[0] ?? null;
  }
  return null;
}

/**
 * Finish any login step that returned a full session: store tokens + user,
 * then hydrate company/permissions from /auth/me.
 */
export async function completeLogin(ok: LoginOk): Promise<MeResponse> {
  useSession.getState().signIn(ok.tokens, ok.user);
  const me = await authApi.me();
  useSession.getState().setIdentity(me);
  return me;
}

/**
 * App boot: if a persisted session exists, re-validate it against /auth/me.
 * 401/403 clears the stale session; a network failure keeps cached identity
 * so the shell can render (requests will surface the outage individually).
 */
export async function bootstrapSession(): Promise<void> {
  const state = useSession.getState();
  if (!state.tokens) {
    state.setBootstrapped(true);
    return;
  }
  try {
    const me = await authApi.me();
    useSession.getState().setIdentity(me);
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      (error.statusCode === 401 || error.statusCode === 403)
    ) {
      useSession.getState().clear();
    } else {
      useSession.getState().setBootstrapped(true);
    }
  }
}

/** Sign out: best-effort server revocation, then drop local state. */
export async function signOut(): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    // Local sign-out proceeds even if the API is unreachable.
  }
  useSession.getState().clear();
}
